/**
 * @file StoryItem.jsx
 * @description Component to display a story item with options to play, edit, duplicate, and delete stories.
 * @requires React, react-router-dom, PropTypes, short-unique-id, react-i18next, firebase/firestore, firebase/storage, semantic-ui-react
 * @exports StoryItem
 */

import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import ShortUniqueId from 'short-unique-id'
import { useTranslation } from 'react-i18next'
import { doc, getDoc, deleteDoc } from 'firebase/firestore'
import { Button, Popup } from 'semantic-ui-react'

import { db } from '../firebaseConfig'
import { deleteFolder } from '../Utils/Utils'
import { StoryContext } from '../Context/StoryContext'
import { EnvironmentContext } from '../Context/EnvironmentContext'
import PlayButton from '../Utils/ActionButtons/PlayButton'
import ModifyButton from '../Utils/ActionButtons/ModifyButton'
import DuplicateButton from '../Utils/ActionButtons/DuplicateButton'
import DeleteButton from '../Utils/ActionButtons/DeleteButton'
import LikeButton from '../Utils/ActionButtons/LikeButton'
import { duplicateStory } from '../Stories/StorageUtils'
import {
    adaptEventsToOldFormat,
    adaptMessagesToOldFormat,
} from './adapt_old_format'
import ShareButton from '../Utils/ActionButtons/ShareButton'

import './CreateListStories.css'

/**
 * @typedef {Object} StoryElement
 * @description An object containing details about a story.
 * @property {string} id - The unique ID of the story.
 * @property {string} idDoc - The unique ID of the story document.
 * @property {Object} parameters - The parameters of the story.
 * @property {Date} creation_date - The creation date of the story.
 */

/**
 * StoryItem is a component to display a story item with options to play, edit, duplicate, and delete stories.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {StoryElement} props.storyElement - The story object containing details for rendering.
 * @param {Function} props.fetchStoriesFunction - Callback function to fetch the list of stories.
 * @param {boolean} [alreadyStarted=false] - Flag to indicate if the story has already been started.
 * @param {boolean} [editActived=false] - Flag to indicate if the edit button is active.
 * @param {boolean} [duplicateActived=false] - Flag to indicate if the duplicate button is active.
 * @param {boolean} [deleteActived=false] - Flag to indicate if the delete button is active.
 * @returns {JSX.Element} A styled story item component with options to play, edit, duplicate, and delete stories.
 *
 * @example
 * // Basic usage
 * <StoryItem
 *     storyElement={{
 *         id: 'story1',
 *         idDoc: 'document1',
 *         parameters: {
 *             name: 'Story 1',
 *             thumbnail: 'thumbnail1.png',
 *     },
 *     fetchStoriesFunction={fetchStories}
 * />
 *
 * // Customized story item
 * <StoryItem
 *     storyElement={{
 *         id: 'story2',
 *         idDoc: 'document2',
 *         parameters: {
 *             name: 'Story 2',
 *             thumbnail: 'thumbnail2.png',
 *     },
 *     fetchStoriesFunction={fetchStories}
 *     alreadyStarted
 *     editActived
 *     duplicateActived
 *     deleteActived
 * />
 */

const StoryItem = ({
    storyElement,
    fetchStoriesFunction,
    alreadyStarted = false,
    editActived = false,
    duplicateActived = false,
    deleteActived = false,
    likeActived = false,
    minimalView = false,
}) => {
    const storyContext = useContext(StoryContext)
    const env = useContext(EnvironmentContext)
    const { t } = useTranslation()
    const navigate = useNavigate()

    const elementId = alreadyStarted ? storyElement.idDoc : storyElement.id

    const changeStory = async (id, saveId) => {
        if (env.setUuidOfCurrentStory) {
            if (!saveId) {
                env.setAlreadyStartedStories(alreadyStarted)
                env.setUuidOfCurrentStory(id)
            } else {
                await env.setAlreadyStartedStories(alreadyStarted)

                // Correctly use template literals with backticks for variable substitution
                const storyRef = doc(
                    db,
                    `users/${env.user.uid}/stories/${saveId}`
                )
                const docSnap = await getDoc(storyRef)

                if (docSnap.exists()) {
                    // Check if the document exists
                    const storyData = docSnap.data()

                    const useOldFormat = (storyData.formatVersion || 1) < 2

                    // Assuming setInitialContactList and setMessagesList update your component's state
                    await storyContext.setContactList(storyData.contacts)
                    await storyContext.setMessagesList(
                        useOldFormat
                            ? adaptMessagesToOldFormat(storyData)
                            : storyData.messages || []
                    )
                    await storyContext.setVariablesList(storyData.variables || [])
                    await storyContext.setEventsList(
                        useOldFormat
                            ? adaptEventsToOldFormat(storyData)
                            : storyData.events || []
                    )
                    await storyContext.setSelectedContact(storyData.selectedContact || '')
                    await storyContext.setRessourcesComplexes(storyData.ressources || [])
                    
                    // Convert start_date to Date object if needed
                    let startDate = storyData.currentTime?.toDate?.() || storyData.currentTime;
                    if (!startDate) {
                        startDate = storyData.parameters?.start_date;
                    }
                    if (startDate && !(startDate instanceof Date)) {
                        startDate = startDate.toDate?.() || new Date(startDate);
                    }
                    
                    await storyContext.setParameters({
                        ...storyContext.parameters,
                        ...storyData.parameters,
                        start_date: startDate || new Date(),
                    })
                    env.setUuidOfCurrentStory(storyData.id)
                } else {
                    console.log('No such document!')
                }
            }
        }
    }

    /**
     * handleStoryClick is a helper function to handle the click event on a story item.
     *
     * @function handleStoryClick
     * @param {string} storyId The ID of the story to navigate to.
     * @param {string} [saveId=null] The ID of the save to load if the story has already been started.
     * @returns {void}
     *
     * @example
     * handleStoryClick('story1');
     * handleStoryClick('story2', 'save1');
     */
    const handleStoryClick = async (storyId, saveId = null) => {
        let path = `/Story/${storyId}`
        if (env.flyMode) {
            path += '?fly=true'
        }
        navigate(path)
        await changeStory(storyId, saveId)
    }

    /**
     * handleEditClick is a helper function to handle the click event on the edit button.
     *
     * @function handleEditClick
     * @param {string} storyId The ID of the story to navigate to.
     * @returns {void}
     *
     * @example
     * handleEditClick('story1');
     */
    const handleEditClick = (storyId) => {
        navigate(`/Create/Edit/${storyId}`)
    }

    /**
     * handleDuplicateClick is a helper function to handle the click event on the duplicate button.
     *
     * @function handleDuplicateClick
     * @param {string} storyId The ID of the story to duplicate.
     * @returns {void}
     *
     * @example
     * handleDuplicateClick('story1');
     */
    const handleDuplicateClick = async (storyId) => {
        const newId = new ShortUniqueId({ length: 20 }).randomUUID()
        const result = await duplicateStory(storyId, newId, env.user.uid)
        if (result) navigate(`/Create/Edit/${newId}`)
    }

    /**
     * handleDeleteClick is a helper function to handle the click event on the delete button.
     *
     * @function handleDeleteClick
     * @param {string} storyId The ID of the story to delete.
     * @param {boolean} alreadyStarted Flag to indicate if the story has already been started.
     * @returns {void}
     *
     * @example
     * handleDeleteClick('story1', false);
     * handleDeleteClick('story2', true);
     */
    const handleDeleteClick = async (storyId, alreadyStarted) => {
        if (env.user) {
            if (!alreadyStarted) {
                const storyRef = doc(db, `stories/${storyId}`)
                const docSnap = await getDoc(storyRef)
                if (docSnap.exists() && storyId !== '') {
                    // Delete the folder from the storage
                    await deleteFolder(`contacts/icons/${storyId}`)
                    await deleteFolder(`musics/${storyId}`)
                    await deleteFolder(`thumbnails/${storyId}`)
                    await deleteFolder(`RessourcesComplexes/${storyId}`)

                    // Delete the document
                    await deleteDoc(storyRef)

                    // Refresh the list of stories
                    fetchStoriesFunction()
                } else {
                    console.log('No such document!')
                }
            } else {
                const storyRef = doc(
                    db,
                    `users/${env.user.uid}/stories/${storyId}`
                )
                const docSnap = await getDoc(storyRef)
                if (docSnap.exists()) {
                    await deleteDoc(storyRef)

                    // Refresh the list of stories
                    fetchStoriesFunction()
                } else {
                    console.log('No such document!')
                }
            }
        }
    }

    return (
        <Popup
            content={
                <div>
                    <strong>{storyElement.parameters.name || 'Unnamed Story'}</strong>
                    {storyElement.parameters.author && <div>{t('By')} {storyElement.parameters.author}</div>}
                    <div>{alreadyStarted ? t('SaveDate') : t('CreationDate')} {storyElement.creation_date ? new Date(storyElement.creation_date.seconds * 1000).toLocaleDateString() : 'No date'}</div>
                    {storyElement.parameters.home_message || t('No description')}
                </div>
            }
            trigger={
                <div
                    key={storyElement.id}
                    className="story-item"
                    onClick={async () => {
                        handleStoryClick(
                            storyElement.id,
                            alreadyStarted ? storyElement.idDoc : null
                        )
                    }}
                >
                    <img
                        src={
                            storyElement.parameters.thumbnail ||
                            'imageUnavailable.png'
                        }
                        alt="Story Visual"
                    />
                    <div className="story-info">
                        <div className="story-name">
                            {storyElement.parameters.name || 'Unnamed Story'}
                        </div>
                        {storyElement.parameters.author && (
                            <div className="story-author">
                                {t('By')} {storyElement.parameters.author}
                            </div>
                        )}
                        {storyElement.parameters.genres && storyElement.parameters.genres.length > 0 && (
                            <div className="story-genres">
                                {storyElement.parameters.genres.map((genre, index) => (
                                    <span key={index} className={`genre-tag genre-${index % 5}`}>
                                        {genre}
                                    </span>
                                ))}
                            </div>
                        )}
                        {alreadyStarted && (
                            <div className="story-description">{`${alreadyStarted ? t('SaveDate') : t('CreationDate')} ${storyElement.creation_date ? new Date(storyElement.creation_date.seconds * 1000).toLocaleDateString() : 'No date'}`}</div>
                        )}
                    </div>
                    <Button.Group>
                        {!minimalView && (
                            <PlayButton
                                action={handleStoryClick}
                                elementId={storyElement.id}
                                saveId={
                                    alreadyStarted ? storyElement.idDoc : null
                                }
                                label={'Play to this story'}
                            />
                        )}
                        {!minimalView && editActived && (
                            <ModifyButton
                                action={handleEditClick}
                                elementId={storyElement.id}
                                label="Navigate to Creation Mode"
                            />
                        )}
                        {!minimalView && duplicateActived && (
                            <DuplicateButton
                                action={handleDuplicateClick}
                                elementId={storyElement.id}
                                label="Duplicate the story"
                            />
                        )}
                        {!minimalView && (
                            <ShareButton
                                icon="share alternate"
                                customClassName={
                                    'ui blue icon button element-buttons'
                                }
                                elementId={storyElement.id}
                            label="Share this story"
                            tooltipText="Share"                            />
                        )}
                        {!minimalView && deleteActived && (
                            <DeleteButton
                                action={handleDeleteClick}
                                elementId={elementId}
                                confirmationText={t('Popup_TextConfirmDelete')}
                                label="Delete the story"
                                alreadyStarted={alreadyStarted}
                            />
                        )}
                        {!minimalView && likeActived && (
                            <LikeButton
                                elementId={storyElement.id}
                                labelLiked={t('Default_AriaLabelLike')}
                                labelUnliked={t('Default_AriaLabelUnlike')}
                                tooltipText={t('Popup_Like')}
                            />
                        )}
                    </Button.Group>
                </div>
            }
            on="hover"
            position="top center"
            wide
            mouseEnterDelay={300}
        />
    )
}

StoryItem.propTypes = {
    storyElement: PropTypes.shape({
        id: PropTypes.string.isRequired,
        idDoc: PropTypes.string,
        parameters: PropTypes.object.isRequired,
        creation_date: PropTypes.instanceOf(Date),
    }).isRequired,
    fetchStoriesFunction: PropTypes.func.isRequired,
    alreadyStarted: PropTypes.bool,
    editActived: PropTypes.bool,
    duplicateActived: PropTypes.bool,
    deleteActived: PropTypes.bool,
}

export default StoryItem
